import { useState } from "react";

import { Button } from "@/components/ui/button";
import type {
  AssistantBlock,
  ChartBlockPayload,
  MapBlockPayload,
  TableBlockPayload,
} from "@/features/assistant/types";

import { BlockChart } from "./BlockChart";
import { BlockMap } from "./BlockMap";
import { BlockTable } from "./BlockTable";

type View = "table" | "bar" | "line";

function tableToChart(
  t: TableBlockPayload,
  kind: "bar" | "line",
): ChartBlockPayload | null {
  const xKey = t.columns[0]?.key;
  const series = t.columns
    .slice(1)
    .filter((c) =>
      t.rows.every((r) => r[c.key] == null || typeof r[c.key] === "number"),
    );
  if (!xKey || series.length === 0) return null;
  return { kind, x_key: xKey, series, rows: t.rows };
}

function chartToTable(c: ChartBlockPayload): TableBlockPayload {
  return {
    columns: [{ key: c.x_key, label: c.x_key }, ...c.series],
    rows: c.rows,
  };
}

export function BlockView({ block }: { block: AssistantBlock }) {
  const initial: View =
    block.type === "chart"
      ? (block.payload as ChartBlockPayload).kind === "line"
        ? "line"
        : "bar"
      : "table";
  const [view, setView] = useState<View>(initial);

  if (block.type === "map") {
    return (
      <figure
        className="my-2 space-y-1"
        data-testid={`assistant-block-${block.id}`}
      >
        <figcaption className="text-xs font-medium text-muted-foreground">
          {block.title}
        </figcaption>
        <BlockMap payload={block.payload as MapBlockPayload} />
      </figure>
    );
  }

  const isPie =
    block.type === "chart" &&
    (block.payload as ChartBlockPayload).kind === "pie";
  const table =
    block.type === "table"
      ? (block.payload as TableBlockPayload)
      : chartToTable(block.payload as ChartBlockPayload);
  const chart = (kind: "bar" | "line") =>
    block.type === "chart"
      ? { ...(block.payload as ChartBlockPayload), kind }
      : tableToChart(block.payload as TableBlockPayload, kind);
  const canChart = chart("bar") !== null;

  return (
    <figure
      className="my-2 space-y-1"
      data-testid={`assistant-block-${block.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <figcaption className="text-xs font-medium text-muted-foreground">
          {block.title}
        </figcaption>
        {canChart && !isPie && (
          <div className="flex gap-1" data-testid="assistant-block-format">
            {(["table", "bar", "line"] as View[]).map((v) => (
              <Button
                key={v}
                size="sm"
                variant={view === v ? "secondary" : "ghost"}
                className="h-6 px-2 text-xs"
                onClick={() => setView(v)}
                data-testid={`assistant-block-format-${v}`}
              >
                {v === "table" ? "Tabela" : v === "bar" ? "Barras" : "Linha"}
              </Button>
            ))}
          </div>
        )}
      </div>
      {isPie ? (
        <BlockChart payload={block.payload as ChartBlockPayload} />
      ) : view === "table" ? (
        <BlockTable payload={table} />
      ) : (
        <BlockChart payload={chart(view)!} />
      )}
    </figure>
  );
}
